import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, Outlet, useParams, useLocation } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import CallPerformanceAnalytics from './pages/call-performance-analytics';
import ProspectPipelineMonitor from './pages/prospect-pipeline-monitor';
import SalesOverviewDashboard from './pages/sales-overview-dashboard';
import TeamManagement from './pages/team-management';
import AccountsManage from './pages/accounts-manage';
import Gallery from './pages/gallery';
import ContactIntelligenceHub from './pages/contact-intelligence-hub';
import ProspectContactDirectory from './pages/prospect-contact-directory';
import ProspectImportPage from './pages/prospect-contact-directory/ProspectImportPage';
import ProspectEntrepriseSearchPage from './pages/prospect-contact-directory/ProspectEntrepriseSearchPage';
import { canManageCrmCampaigns } from './utils/crmAccess';
import Campaigns from './pages/campaigns';
import CampaignCreatePage from './pages/campaigns/CampaignCreatePage';
import CampaignWorkspaceLayout from './pages/campaigns/CampaignWorkspaceLayout';
import CampaignProductsTab from './pages/campaigns/CampaignProductsTab';
import CampaignEmailHubLayout from './pages/campaigns/email/CampaignEmailHubLayout';
import CampaignEmailHubupRoute from './pages/campaigns/email/CampaignEmailHubupRoute';
import CampaignEmailLibraryRoute from './pages/campaigns/email/CampaignEmailLibraryRoute';
import CampaignEmailTemplateRoute from './pages/campaigns/email/CampaignEmailTemplateRoute';
import CampaignEmailSendRoute from './pages/campaigns/email/CampaignEmailSendRoute';
import Login from './pages/auth/Login';
import Merci from './pages/Merci';
import HubupDashboard from './pages/hubup/HubupDashboard';
import HubupSubjects from './pages/hubup/HubupSubjects';
import HubupNotifications from './pages/hubup/HubupNotifications';
import HubupCommunitiesManage from './pages/hubup/HubupCommunitiesManage';
import HubupAnnuaryApprovals from './pages/hubup/HubupAnnuaryApprovals';
import HubupNotificationFormResponses from './pages/hubup/HubupNotificationFormResponses';
import HubupFormAnnuaire from './pages/hubup/HubupFormAnnuaire';
import HubupPublicProfile from './pages/hubup/HubupPublicProfile';
import HubupEntry from './pages/hubup/HubupEntry';
import HubupGroupLayout from './pages/hubup/HubupGroupLayout';
import { UserAccessProvider, useUserAccess } from './context/UserAccessContext';
import { CompanyProvider, useCompaniesOptional } from './context/CompanyContext';
import { HubupCommunitiesProvider } from './context/HubupCommunitiesContext';
import AppChooserModal from './components/AppChooserModal';
import {
  canOpenEnterpriseApp,
  getDefaultCrmPath,
  getDefaultEnterprisePath,
  getDefaultWebCreatorPath,
} from './access/userAccess';
import EnterpriseManageHome from './pages/enterprise-manage/EnterpriseManageHome';
import EnterpriseManageLayout from './pages/enterprise-manage/EnterpriseManageLayout';
import EnterpriseSummaryTab from './pages/enterprise-manage/EnterpriseSummaryTab';
import EnterpriseTeamTab from './pages/enterprise-manage/EnterpriseTeamTab';
import EnterpriseRolesTab from './pages/enterprise-manage/EnterpriseRolesTab';
import EnterpriseSettingsTab from './pages/enterprise-manage/EnterpriseSettingsTab';
import CompaniesManage from './pages/companies/CompaniesManage';
import SalesAdmin from './pages/admin/SalesAdmin';
import AffiliateTracking from './pages/affiliate/AffiliateTracking';
import CrmConfiguration from './pages/crm-configuration/index';
import WebCreatorHome from './pages/web-creator/index';
import WebCreatorEditor from './pages/web-creator/WebCreatorEditor';
import WebCreatorCommerceAdmin from './pages/web-creator/WebCreatorCommerceAdmin';
import WebCreatorPublic from './pages/web-creator/WebCreatorPublic';
import { fetchMyHubupCommunities } from './api/hubup';

const isAuthenticated = () => !!localStorage.getItem('authToken');

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
    Chargement...
  </div>
);

const PrivateRoute = ({ element }) => {
  const { loading } = useUserAccess();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading) return <LoadingScreen />;
  return element;
};

const PostLoginRedirect = () => {
  const { user, profile, activeApp, chooserOpen } = useUserAccess();
  if (chooserOpen && profile.kind === 'dual') {
    return null;
  }
  if (profile.kind === 'hubup_only' || (profile.kind === 'dual' && activeApp === 'hubup')) {
    return <Navigate to="/hubup" replace />;
  }
  if (profile.kind === 'dual' && activeApp === 'web-creator') {
    return <Navigate to={getDefaultWebCreatorPath()} replace />;
  }
  if (profile.kind === 'crm_only' && activeApp === 'web-creator') {
    return <Navigate to={getDefaultWebCreatorPath()} replace />;
  }
  if (activeApp === 'enterprise' && canOpenEnterpriseApp(user)) {
    return <Navigate to={getDefaultEnterprisePath()} replace />;
  }
  return <Navigate to={getDefaultCrmPath(user)} replace />;
};

const CampaignManagerRoute = ({ element }) => {
  const { user, profile, activeApp, loading } = useUserAccess();
  const companies = useCompaniesOptional();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading || companies?.loading) return <LoadingScreen />;
  if (profile.kind === 'dual' && !activeApp) {
    return <Navigate to="/" replace />;
  }
  if (!canManageCrmCampaigns(user)) {
    return <Navigate to="/campaigns" replace />;
  }
  return element;
};

const CrmRoute = ({ element }) => {
  const { user, profile, activeApp, loading } = useUserAccess();
  const companies = useCompaniesOptional();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading || companies?.loading) return <LoadingScreen />;
  if (profile.kind === 'dual' && !activeApp) {
    return <Navigate to="/" replace />;
  }
  if (profile.kind === 'hubup_only') {
    return <Navigate to="/hubup" replace />;
  }
  if (profile.kind === 'dual' && activeApp === 'hubup') {
    return <Navigate to="/hubup" replace />;
  }
  if (
    user?.permission !== 'admin' &&
    companies?.noAssignment
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center text-sm text-muted-foreground">
        Aucune entreprise ne vous est assignée. Contactez un administrateur.
      </div>
    );
  }
  return element;
};

const TeamLeadCrmRoute = ({ element }) => {
  const { user, profile, activeApp, loading } = useUserAccess();
  const companies = useCompaniesOptional();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading || companies?.loading) return <LoadingScreen />;
  if (profile.kind === 'dual' && !activeApp) {
    return <Navigate to="/" replace />;
  }
  if (profile.kind === 'hubup_only') {
    return <Navigate to="/hubup" replace />;
  }
  if (profile.kind === 'dual' && activeApp === 'hubup') {
    return <Navigate to="/hubup" replace />;
  }
  if (user?.permission === 'admin') {
    return <Navigate to="/accounts-manage" replace />;
  }
  if (user?.permission !== 'team' || user?.crm !== true) {
    return <Navigate to={getDefaultCrmPath(user)} replace />;
  }
  if (companies?.noAssignment) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center text-sm text-muted-foreground">
        Aucune entreprise ne vous est assignée. Contactez un administrateur.
      </div>
    );
  }
  return element;
};

const AdminRoute = ({ element }) => {
  const { user, loading } = useUserAccess();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading) return <LoadingScreen />;
  if (user?.permission !== 'admin') {
    return <Navigate to={getDefaultCrmPath(user)} replace />;
  }
  return element;
};

const AdminAccountsPage = () => {
  const { user, profile, activeApp, loading } = useUserAccess();
  const location = useLocation();
  const [hubupRedirect, setHubupRedirect] = useState(null);
  const [hubupRedirectLoading, setHubupRedirectLoading] = useState(false);

  const inHubupApp =
    profile?.kind === 'hubup_only' ||
    (profile?.kind === 'dual' && activeApp === 'hubup');

  useEffect(() => {
    if (!inHubupApp || location.pathname !== '/accounts-manage') {
      return;
    }
    let cancelled = false;
    setHubupRedirectLoading(true);
    fetchMyHubupCommunities()
      .then((res) => {
        if (cancelled) return;
        const id = res?.data?.communities?.[0]?.id;
        if (id) {
          setHubupRedirect(`/hubup/g/${id}/accounts-manage`);
        }
      })
      .finally(() => {
        if (!cancelled) setHubupRedirectLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [inHubupApp, location.pathname]);

  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading || (inHubupApp && hubupRedirectLoading)) {
    return <LoadingScreen />;
  }
  if (user?.permission !== 'admin') {
    return <Navigate to={getDefaultCrmPath(user)} replace />;
  }
  if (hubupRedirect) {
    return <Navigate to={hubupRedirect} replace />;
  }
  return <AccountsManage />;
};

const AdminCrmRoute = ({ element }) => {
  const { user, loading } = useUserAccess();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading) return <LoadingScreen />;
  if (user?.permission !== 'admin') {
    return <Navigate to={getDefaultCrmPath(user)} replace />;
  }
  return <CrmRoute element={element} />;
};

const AffiliateRoute = ({ element }) => {
  const { user, profile, activeApp, loading } = useUserAccess();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading) return <LoadingScreen />;
  if (!user?.affiliateCode?.trim()) {
    if (profile?.kind === 'hubup_only' || (profile?.kind === 'dual' && activeApp === 'hubup')) {
      return <Navigate to="/hubup" replace />;
    }
    return <Navigate to={getDefaultCrmPath(user)} replace />;
  }
  return element;
};

const HubupRoute = ({ element }) => {
  const location = useLocation();
  const { user, profile, activeApp, loading } = useUserAccess();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading) return <LoadingScreen />;
  if (profile.kind === 'dual' && !activeApp) {
    return <Navigate to="/" replace />;
  }
  if (profile.kind === 'crm_only') {
    return <Navigate to={getDefaultCrmPath(user)} replace />;
  }
  const inHubupCommunity = /^\/hubup\/g\/[^/]+/.test(location.pathname);
  if (profile.kind === 'dual' && activeApp === 'crm' && !inHubupCommunity) {
    return <Navigate to={getDefaultCrmPath(user)} replace />;
  }
  return element;
};

const HubupNestedOutlet = () => (
  <HubupCommunitiesProvider>
    <Outlet />
  </HubupCommunitiesProvider>
);

const HubupGroupIndexRedirect = () => {
  const { groupId } = useParams();
  return <Navigate to={`/hubup/g/${groupId}/dashboard`} replace />;
};

const HubupAdminAccountsRoute = () => {
  const { groupId } = useParams();
  const { user, loading } = useUserAccess();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading) return <LoadingScreen />;
  if (user?.permission !== 'admin') {
    return (
      <Navigate to={`/hubup/g/${groupId}/dashboard`} replace />
    );
  }
  return <AccountsManage />;
};

const HubupToRoot = () => <Navigate to="/hubup" replace />;

const WebCreatorRoute = ({ element }) => {
  const { loading } = useUserAccess();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading) return <LoadingScreen />;
  return element;
};

const EnterpriseRoute = ({ element }) => {
  const { user, loading } = useUserAccess();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading) return <LoadingScreen />;
  if (!canOpenEnterpriseApp(user)) {
    return <Navigate to={getDefaultCrmPath(user)} replace />;
  }
  return element;
};

const ConfigCrmRoute = ({ element }) => {
  const { user, profile, activeApp, loading } = useUserAccess();
  const companies = useCompaniesOptional();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading || companies?.loading) return <LoadingScreen />;
  if (profile.kind === 'dual' && !activeApp) {
    return <Navigate to="/" replace />;
  }
  if (profile.kind === 'hubup_only') {
    return <Navigate to="/hubup" replace />;
  }
  if (profile.kind === 'dual' && activeApp === 'hubup') {
    return <Navigate to="/hubup" replace />;
  }
  const canManage =
    user?.permission === 'admin' ||
    (user?.permission === 'team' && user?.crm === true);
  if (!canManage) {
    return <Navigate to={getDefaultCrmPath(user)} replace />;
  }
  if (user?.permission !== 'admin' && companies?.noAssignment) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center text-sm text-muted-foreground">
        Aucune entreprise ne vous est assignée. Contactez un administrateur.
      </div>
    );
  }
  return element;
};

function CampaignEmailLegacyRedirect() {
  const { id } = useParams();
  return <Navigate to={`/campaigns/${encodeURIComponent(id)}/email`} replace />;
}

const RoutesInner = () => {
  return (
    <>
      <ScrollToTop />
      <RouterRoutes>
        <Route path="/login" element={<Login />} />
        <Route path="/merci" element={<Merci />} />
        <Route path="/lp/:slug/commande" element={<WebCreatorPublic />} />
        <Route path="/lp/:slug" element={<WebCreatorPublic />} />
        <Route path="/" element={<PrivateRoute element={<PostLoginRedirect />} />} />
        <Route path="/sales-overview-dashboard" element={<CrmRoute element={<SalesOverviewDashboard />} />} />
        <Route path="/team-management" element={<TeamLeadCrmRoute element={<TeamManagement />} />} />
        <Route path="/accounts-manage" element={<AdminAccountsPage />} />
        <Route path="/companies-manage" element={<AdminCrmRoute element={<CompaniesManage />} />} />
        <Route path="/sales-admin" element={<AdminCrmRoute element={<SalesAdmin />} />} />
        <Route path="/affiliate-tracking" element={<AffiliateRoute element={<AffiliateTracking />} />} />
        <Route path="/call-performance-analytics" element={<CrmRoute element={<CallPerformanceAnalytics />} />} />
        <Route path="/prospect-pipeline-monitor" element={<CrmRoute element={<ProspectPipelineMonitor />} />} />
        <Route path="/contact-intelligence-hub" element={<CrmRoute element={<ContactIntelligenceHub />} />} />
        <Route path="/gallery" element={<CrmRoute element={<Gallery />} />} />
        <Route path="/prospect-contact-directory" element={<CrmRoute element={<ProspectContactDirectory />} />} />
        <Route
          path="/prospect-contact-directory/import"
          element={<CrmRoute element={<ProspectImportPage />} />}
        />
        <Route
          path="/prospect-contact-directory/search"
          element={<CrmRoute element={<ProspectEntrepriseSearchPage />} />}
        />
        <Route path="/campaigns" element={<CrmRoute element={<Campaigns />} />} />
        <Route
          path="/campaigns/new"
          element={<CampaignManagerRoute element={<CampaignCreatePage />} />}
        />
        <Route path="/campaigns/:id" element={<CrmRoute element={<CampaignWorkspaceLayout />} />}>
          <Route index element={<Navigate to="products" replace />} />
          <Route path="products" element={<CampaignProductsTab />} />
          <Route path="hubup" element={<CampaignEmailHubupRoute />} />
          <Route path="email" element={<CampaignEmailHubLayout />}>
            <Route index element={<CampaignEmailLibraryRoute />} />
            <Route path="send" element={<CampaignEmailSendRoute />} />
            <Route path="send/:templateId" element={<CampaignEmailSendRoute />} />
          </Route>
          <Route
            path="email/templates/new"
            element={<CampaignManagerRoute element={<CampaignEmailTemplateRoute />} />}
          />
          <Route
            path="email/templates/:templateId"
            element={<CampaignManagerRoute element={<CampaignEmailTemplateRoute />} />}
          />
        </Route>
        <Route
          path="/campaigns/:id/emailing"
          element={<CrmRoute element={<CampaignEmailLegacyRedirect />} />}
        />
        <Route path="/crm-configuration" element={<ConfigCrmRoute element={<CrmConfiguration />} />} />
        <Route
          path="/enterprise-manage"
          element={<EnterpriseRoute element={<EnterpriseManageHome />} />}
        />
        <Route
          path="/enterprise-manage/:enterpriseId"
          element={<EnterpriseRoute element={<EnterpriseManageLayout />} />}
        >
          <Route index element={<EnterpriseSummaryTab />} />
          <Route path="equipe" element={<EnterpriseTeamTab />} />
          <Route path="roles" element={<EnterpriseRolesTab />} />
          <Route path="permissions" element={<Navigate to="../roles" replace />} />
          <Route path="parametres" element={<EnterpriseSettingsTab />} />
        </Route>
        <Route path="/web-creator" element={<WebCreatorRoute element={<WebCreatorHome />} />} />
        <Route path="/web-creator/edit/:id" element={<WebCreatorRoute element={<WebCreatorEditor />} />} />
        <Route
          path="/web-creator/edit/:id/administration"
          element={<WebCreatorRoute element={<WebCreatorCommerceAdmin />} />}
        />
        <Route path="/hubup/dashboard" element={<HubupRoute element={<HubupToRoot />} />} />
        <Route path="/hubup/subjects" element={<HubupRoute element={<HubupToRoot />} />} />
        <Route path="/hubup/community" element={<HubupRoute element={<HubupToRoot />} />} />
        <Route path="/hubup/notifications" element={<HubupRoute element={<HubupToRoot />} />} />
        <Route path="/hubup" element={<HubupRoute element={<HubupNestedOutlet />} />}>
          <Route index element={<HubupEntry />} />
          <Route path="g/:groupId" element={<HubupGroupLayout />}>
            <Route index element={<HubupGroupIndexRedirect />} />
            <Route path="dashboard" element={<HubupDashboard />} />
            <Route path="subjects" element={<HubupSubjects />} />
            <Route path="community" element={<HubupGroupIndexRedirect />} />
            <Route path="notifications" element={<HubupNotifications />} />
            <Route path="form-annuaire" element={<HubupFormAnnuaire />} />
            <Route path="public-profile" element={<HubupPublicProfile />} />
            <Route
              path="notifications/:messageId/form-responses"
              element={<HubupNotificationFormResponses />}
            />
            <Route path="communities-manage" element={<HubupCommunitiesManage />} />
            <Route path="annuary-approvals" element={<HubupAnnuaryApprovals />} />
            <Route path="accounts-manage" element={<HubupAdminAccountsRoute />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      <AppChooserModal />
    </>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <UserAccessProvider>
          <CompanyProvider>
            <RoutesInner />
          </CompanyProvider>
        </UserAccessProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
